import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import { apiRequest } from "@mhj/shared/api";
import { CATEGORY_LABELS } from "../lib/scholarship";
import { useAuth } from "@mhj/shared/auth";
import type { ScholarshipApplication } from "@mhj/shared/types";

export default function PortalDashboard() {
  const { user } = useAuth();
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["scholarship", "me", user?.id ?? "anonymous"],
    enabled: !!user,
    queryFn: () =>
      apiRequest<{ application: ScholarshipApplication | null }>("/scholarships/me"),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });

  const application = data?.application ?? null;

  useEffect(() => {
    if (error) {
      toast.error((error as Error).message);
    }
  }, [error]);

  if (isLoading) {
    return (
      <div className="glass-card rounded-3xl p-6">
        <p className="text-sm text-black/70">Loading your application…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="glass-card rounded-3xl p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-blue-300">
          Scholarship status
        </p>
        {application ? (
          <>
            <h2 className="mt-3 text-2xl font-semibold text-black">
              {CATEGORY_LABELS[application.category]} application
            </h2>
            <p className="mt-2 text-sm text-black/60">
              Status:{" "}
              <span className="font-semibold uppercase text-blue-300">
                {application.status}
              </span>
            </p>
            <div className="mt-4 grid gap-3 text-xs text-black/60 md:grid-cols-2">
              <div>
                LGA:{" "}
                <span className="text-black/90">{application.lga}</span>
              </div>
              <div>
                State:{" "}
                <span className="text-black/90">{application.state}</span>
              </div>
              <div>
                Submitted:{" "}
                <span className="text-black/90">
                  {application.submittedAt ? "Yes" : "Not yet"}
                </span>
              </div>
              <div>
                Documents:{" "}
                <span className="text-black/90">
                  {application.documents.length}
                </span>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/portal/apply"
                className="rounded-full bg-blue-500 px-5 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-blue-400"
              >
                Continue application
              </Link>
              <button
                onClick={() => void refetch()}
                className="rounded-full border border-white/20 px-5 py-3 text-xs uppercase tracking-[0.3em] text-blue-500 transition pointer hover:border-blue-400 hover:text-blue-200"
              >
                Refresh status
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="mt-3 text-2xl font-semibold text-black">
              No application yet
            </h2>
            <p className="mt-2 text-sm text-black/60">
              Start your scholarship application to upload documents and submit
              for review.
            </p>
            <Link
              to="/portal/apply"
              className="mt-6 inline-flex rounded-full bg-blue-500 px-5 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-black transition hover:bg-blue-400"
            >
              Start application
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
